import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import TeamMemberService from 'services/TeamMemberService';
import { integrationSecretsQuery } from 'query/integration-secrets';
import { getAllNestedInputsFromConfig } from '@kizenapps/engine/util';
import type { SaveSecretFn } from '@kizenapps/engine/util';
import type {
  SetupAssistantConfig,
  SetupAssistantField,
  UnknownJSON,
} from '@kizenapps/engine';

type IntegrationSecretRow = {
  id: string;
  api_name: string;
  description: string;
  obfuscated_value?: string;
  business_plugin_app?: { plugin_app_api_name: string };
};

export const getOwnedSecretNames = (
  setupAssistantConfig?: SetupAssistantConfig,
  userSetupAssistantConfig?: SetupAssistantConfig
): Set<string> => {
  const owned = new Set<string>();

  [setupAssistantConfig, userSetupAssistantConfig].forEach((config) => {
    if (!config) {
      return;
    }

    getAllNestedInputsFromConfig(config).forEach((field) => {
      if (field.type === 'api_key' && field.secret) {
        owned.add(field.secret);
      }
    });
  });

  return owned;
};

export const withApiKeyFieldsUnrequired = (
  fields: SetupAssistantField[] | undefined,
  canEditIntegrationSecrets: boolean
): SetupAssistantField[] | undefined => {
  if (!fields || canEditIntegrationSecrets) {
    return fields;
  }

  return fields.map((field) => {
    if (field.type === 'api_key') {
      return { ...field, required: false };
    }

    if ('fields' in field && field.fields) {
      return {
        ...field,
        fields: withApiKeyFieldsUnrequired(
          field.fields,
          canEditIntegrationSecrets
        ),
      } as SetupAssistantField;
    }

    return field;
  });
};

export const bareSecretName = (
  pluginApiName: string,
  apiName: string
): string =>
  apiName.startsWith(`${pluginApiName}__`)
    ? apiName.slice(pluginApiName.length + 2)
    : apiName;

export const createSaveSecret = (
  pluginApiName: string,
  canEditIntegrationSecrets: boolean
): SaveSecretFn => {
  let rowsByApiName: Promise<Map<string, IntegrationSecretRow>> | null = null;

  const getRowsByApiName = (): Promise<Map<string, IntegrationSecretRow>> => {
    if (!rowsByApiName) {
      // Don't pass `search` — the backend only matches it against description/obfuscated_value/plugin
      // display name, never `api_name`, so it can't narrow this and would silently return nothing.
      rowsByApiName = TeamMemberService.getIntegrationSecrets({
        search: undefined,
        ordering: 'name',
        page: 1,
        page_size: 999,
      }).then(
        ({ results }) =>
          new Map(
            (results as IntegrationSecretRow[])
              .filter(
                (row) =>
                  row.business_plugin_app?.plugin_app_api_name === pluginApiName
              )
              .map((row) => [row.api_name, row])
          )
      );
    }

    return rowsByApiName;
  };

  return async ({ secretName, value }) => {
    if (!canEditIntegrationSecrets) {
      throw new Error(
        `Cannot save secret "${secretName}" for "${pluginApiName}" - the current user does not have permission to manage integration secrets.`
      );
    }

    const apiName = `${pluginApiName}__${secretName}`;
    const existing = (await getRowsByApiName()).get(apiName);

    if (!existing) {
      throw new Error(
        `This app's "${secretName}" secret isn't set up yet. Try again, or reinstall the app.`
      );
    }

    await TeamMemberService.updateIntegrationSecret(existing.id, {
      value,
      description: existing.description,
    });
  };
};

// Callers must hold off rendering the controller until `isLoading` is false — `value` only
// seeds its state once, on mount, so a late-arriving `hasValue`/`maskedValue` is never picked up.
export const useApiKeySecretsSeed = (
  pluginApiName: string,
  setupAssistantConfig: SetupAssistantConfig | undefined,
  existingValues: Record<string, UnknownJSON> | undefined,
  canViewIntegrationSecrets: boolean
): {
  seededValues: Record<string, UnknownJSON> | undefined;
  isLoading: boolean;
} => {
  const apiKeyFields = useMemo(
    () =>
      setupAssistantConfig
        ? getAllNestedInputsFromConfig(setupAssistantConfig).filter(
            (field) => field.type === 'api_key'
          )
        : [],
    [setupAssistantConfig]
  );

  const shouldFetch = apiKeyFields.length > 0 && canViewIntegrationSecrets;

  const { data, isFetching } = useQuery(
    integrationSecretsQuery({
      params: { ordering: 'name', page: 1, page_size: 999 },
      enabled: shouldFetch,
      // Only refetch on mount/invalidate, not on window focus — this query gates seeding the
      // controller's state, so a refocus-triggered refetch mid-edit would hide the whole form.
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    })
  );

  const seededValues = useMemo(() => {
    if (apiKeyFields.length === 0) {
      return existingValues;
    }

    const base = { ...(existingValues ?? {}) };

    apiKeyFields.forEach((field) => {
      if (!field.secret) {
        return;
      }

      const apiName = `${pluginApiName}__${field.secret}`;
      const row = (data?.results as IntegrationSecretRow[] | undefined)?.find(
        (r) =>
          r.api_name === apiName &&
          r.business_plugin_app?.plugin_app_api_name === pluginApiName
      );

      base[field.key] = {
        type: 'api_key',
        hasValue: Boolean(row?.obfuscated_value),
        maskedValue: row?.obfuscated_value,
      } as UnknownJSON;
    });

    return base;
  }, [existingValues, apiKeyFields, data, pluginApiName]);

  return { seededValues, isLoading: apiKeyFields.length > 0 && isFetching };
};
